/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  exhaustMap,
  filter,
  Observable,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { AuthFacade } from '~console/app/~store/facades/auth.facade';
import { SKIP_JWT_INTERCEPTOR } from '../constants/global.constants';
import { AuthenticationService } from '../services/authentication.service';
import { extractFromRequestData } from '@veone/ngx-utilities/form';

export function jwtInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const authFacade = inject(AuthFacade);
  const authService = inject(AuthenticationService);
  const { value: skipJwtInterceptor, request: reqWithoutSkipJwtInterceptor } =
    extractFromRequestData<boolean>(request, 'skipJwtInterceptor');
  const { value: skipJwtInterceptorErrors, request: finalRequest } =
    extractFromRequestData<boolean>(
      reqWithoutSkipJwtInterceptor,
      'skipJwtInterceptorErrors'
    );

  if (
    finalRequest.headers.get('Authorization') ||
    finalRequest.context.get(SKIP_JWT_INTERCEPTOR) === true ||
    skipJwtInterceptor
  ) {
    return next(finalRequest);
  }

  return authFacade.accessToken$.pipe(
    take(1),
    switchMap(accessToken => {
      if (accessToken) {
        authService.lastUsedToken = accessToken.id;
        const clonedRequest = finalRequest.clone({
          setHeaders: {
            authorization: `Bearer ${accessToken.id.trim()}`
          }
        });

        return next(clonedRequest).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              return handleTokenRefresh({
                request: finalRequest,
                next,
                authFacade,
                authService,
                error
              });
            }
            return throwError(() => error);
          })
        );
      }
      return next(finalRequest);
    })
  );
}

function handleTokenRefresh({
  request,
  next,
  authFacade,
  authService,
  error
}: {
  request: HttpRequest<any>;
  next: HttpHandlerFn;
  authFacade: AuthFacade;
  authService: AuthenticationService;
  error: HttpErrorResponse;
}): Observable<HttpEvent<any>> {
  return authFacade.refreshToken$.pipe(
    take(1),
    filter(Boolean),
    switchMap(() => authFacade.refreshAccessToken()),
    exhaustMap(() => authFacade.accessToken$.pipe(take(1))),
    switchMap(accessToken => {
      if (accessToken && accessToken.id !== authService.lastUsedToken) {
        authService.lastUsedToken = accessToken.id;
        return next(
          request.clone({
            setHeaders: { authorization: `Bearer ${accessToken.id.trim()}` }
          })
        );
      }
      return throwError(() => error);
    }),
    catchError(() => throwError(() => error))
  );
}
